import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationAttributeItem } from '@/entities/attributes/model/api/types';
import { AddOptionPopover } from '@/shared/ui';
import { isFilterableAttribute } from '../model/attributeFilters';

type Props = {
  /** Все атрибуты из системной конфигурации */
  availableAttributes: ConversationAttributeItem[];
  /** Пути уже добавленных в панель атрибутов — исключаются из выбора */
  activePaths: ReadonlySet<string>;
  /** Колбэк добавления атрибута по его `path` */
  onAdd: (path: string) => void;
};

/**
 * Кнопка-поповер для добавления атрибута в активную панель фильтров экрана-потребителя.
 * Атрибуты с `dataType = STRUCTURE` и уже добавленные в панель исключены из списка.
 */
export const AttributeFilterPicker: FC<Props> = ({ availableAttributes, activePaths, onAdd }) => {
  const { t } = useTranslation('common');

  const options = useMemo(
    () =>
      availableAttributes
        .filter(isFilterableAttribute)
        .filter((a) => !activePaths.has(a.path))
        .map((a) => ({
          value: a.path,
          label: a.pathDescription || a.name || a.path,
        })),
    [availableAttributes, activePaths],
  );

  return (
    <AddOptionPopover
      label={t('attributeFilters.addAttribute')}
      options={options}
      searchPlaceholder={t('attributeFilters.selectAttribute')}
      emptyMessage={t('attributeFilters.noAttributesFound')}
      onAdd={onAdd}
    />
  );
};
